/* Refill ledger figure for the animated-proof section, registered as 'refill-ledger'.
   Documented in kg-custom-sections.md § animated-proof.
   Fig. 1: a cartridge handle that takes one brand's heads; a year of spent heads drops into a pile
   while the cost climbs. Fig. 2: one safety razor; a blade a month comes out and stacks in a small, square pile.
   Drawn as inline SVG in the follicle plate's 740x760 space. Time-driven: render(t) draws the frame
   at t seconds, so the controller can scrub, loop, or freeze it. */
(function () {
  'use strict';
  var NS = 'http://www.w3.org/2000/svg';

  var AXIS = 318;                                   // razor centre line
  var COUNTER = { x: 70, y: 132, captionY: 182 };
  var FLOOR = { y: 636, x0: 290, x1: 722 };
  var MONTHS = { y: 708, x0: 70, x1: 722, count: 12 };
  var LAST_MONTH = MONTHS.count - 1;
  // Shares of a month's cycle: the spent head lands on the pile as the next head reaches the dock, then the new head snaps on.
  var HEAD_LANDS = 0.62;
  var HEAD_SNAPS = 0.68;
  var CURRENCY_SIGN = '£';
  // Both razors are drawn at 1:1 about a 740-wide plate, then enlarged about its centre line.
  var ZOOM = 1.15;
  var ASSEMBLY = 'translate(370 ' + AXIS + ') scale(' + ZOOM + ') translate(-370 ' + -AXIS + ')';
  var DOCK_X = 370 + (568 - 370) * ZOOM;            // cartridge head centre when clicked on
  var PILE_SCALE = 0.5;
  // Where each spent head comes to rest, in falling order: centre x, centre y, angle (90 = lying flat).
  var PILE = [
    [380, 614, 93], [478, 613, 87], [576, 615, 91], [674, 612, 95], [430, 578, 83], [528, 577, 97],
    [338, 592, 62], [626, 579, 86], [680, 584, 114], [478, 542, 102], [576, 544, 79], [528, 507, 93]
  ];

  // Fig. 1: the first head stops short of the dock with the keyed tongue in view (hold), then clicks on.
  var CART = {
    arrive: [0.4, 1.1], hold: 2.3, snap: 2.45, ring: [1.2, 1.8], leader: [1.6, 2.0], lock: [1.7, 2.2],
    stepOne: 3.8, calloutGone: 4.15,
    cycles: [0.9, 0.8, 0.7, 0.6, 0.55, 0.5, 0.45, 0.45, 0.45, 0.45, 0.45, 0.5],
    end: 13.0, still: 12.1                          // the year's total and the full month row hold until Fig. 2 takes over
  };
  var HOLD_GAP = 40;                                // plate units between head and dock while the tongue shows
  var MIN_CAPTION_PX = 12.6;
  var CAPTION_UNITS = 30;

  function el(name, attrs, parent) {
    var node = document.createElementNS(NS, name);
    for (var key in attrs) node.setAttribute(key, attrs[key]);
    if (parent) parent.appendChild(node);
    return node;
  }
  function clamp(x, lo, hi) { return x < lo ? lo : x > hi ? hi : x; }
  function progress(t, a, b) { return clamp((t - a) / (b - a), 0, 1); }
  function easeOut(u) { return 1 - Math.pow(1 - u, 3); }
  function easeIn(u) { return u * u * u; }
  function easeInOut(u) { return u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2; }
  function r1(n) { return Math.round(n * 10) / 10; }
  function inPounds(amount) { return CURRENCY_SIGN + amount; }   // the one place the sign is added; settings hold figures alone

  // Smooth outline through points (Catmull-Rom as cubic Beziers), continuing from the first point.
  function smoothThrough(pts) {
    var d = '';
    for (var i = 0; i < pts.length - 1; i++) {
      var p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || p2;
      d += 'C' + r1(p1[0] + (p2[0] - p0[0]) / 6) + ' ' + r1(p1[1] + (p2[1] - p0[1]) / 6) + ' ' +
        r1(p2[0] - (p3[0] - p1[0]) / 6) + ' ' + r1(p2[1] - (p3[1] - p1[1]) / 6) + ' ' + r1(p2[0]) + ' ' + r1(p2[1]);
    }
    return d;
  }
  // 45 degree hatching clipped to a rectangle; slope +1 runs down-right, -1 up-right.
  function hatch(x0, y0, x1, y1, pitch, slope, phase) {
    var d = '', step = pitch * Math.SQRT2, c, a, b, shift = ((phase || 0) % step + step) % step;
    if (slope > 0) {
      for (c = y0 - x1 + step / 2 - step + shift; c < y1 - x0; c += step) {
        a = Math.max(x0, y0 - c); b = Math.min(x1, y1 - c);
        if (b - a > 0.5) d += 'M' + r1(a) + ' ' + r1(a + c) + 'L' + r1(b) + ' ' + r1(b + c);
      }
    } else {
      for (c = x0 + y0 + step / 2 - step + shift; c < x1 + y1; c += step) {
        a = Math.max(x0, c - y1); b = Math.min(x1, c - y0);
        if (b - a > 0.5) d += 'M' + r1(a) + ' ' + r1(c - a) + 'L' + r1(b) + ' ' + r1(c - b);
      }
    }
    return d;
  }

  /* ---------- shared furniture: floor, month row, counter ---------- */
  function drawFloor(g) {
    var ground = '';
    for (var x = FLOOR.x0 + 10; x <= FLOOR.x1; x += 12) ground += 'M' + x + ' ' + (FLOOR.y + 3) + 'l-9 9';
    el('path', { 'class': 'rl-d rl-ground', d: ground }, g);
    el('path', { 'class': 'rl-d', d: 'M' + FLOOR.x0 + ' ' + FLOOR.y + 'H' + FLOOR.x1 }, g);
  }
  function MonthRow(g, fillClass, caption) {
    var span = MONTHS.x1 - MONTHS.x0;
    el('text', { 'class': 'rl-cap', x: MONTHS.x0 + 2, y: MONTHS.y - 24 }, g).textContent = caption;
    el('path', { 'class': 'rl-d rl-faint', d: 'M' + MONTHS.x0 + ' ' + MONTHS.y + 'H' + MONTHS.x1 }, g);
    this.fill = el('path', { 'class': 'rl-d ' + fillClass, d: '' }, g);
    this.ticks = [];
    for (var i = 0; i <= MONTHS.count; i++) {
      var x = MONTHS.x0 + span * i / MONTHS.count;
      this.ticks.push(el('path', { 'class': 'rl-d rl-tick', d: 'M' + r1(x) + ' ' + (MONTHS.y - 8) + 'V' + (MONTHS.y + 8) }, g));
    }
    this.span = span;
  }
  MonthRow.prototype.render = function (months) {
    var end = MONTHS.x0 + this.span * months / MONTHS.count;
    this.fill.setAttribute('d', months > 0.01 ? 'M' + MONTHS.x0 + ' ' + MONTHS.y + 'H' + r1(end) : '');
    for (var i = 0; i <= MONTHS.count; i++) this.ticks[i].classList.toggle('is-passed', i > 0 && i <= months + 1e-6);
  };
  function Counter(g) {
    this.value = el('text', { 'class': 'rl-count', x: COUNTER.x, y: COUNTER.y }, g);
    this.caption = el('text', { 'class': 'rl-cap', x: COUNTER.x + 2, y: COUNTER.captionY }, g);
    this.text = null;
  }
  Counter.prototype.render = function (text, captionText, opacity, captionOpacity) {
    if (text !== this.text) { this.value.textContent = text; this.text = text; }
    if (this.caption.textContent !== captionText) this.caption.textContent = captionText;
    this.value.style.opacity = opacity;
    this.caption.style.opacity = captionOpacity;
  };

  // Captions never render below 12.6px, however narrow the plate: grow their units as the plate shrinks.
  function keepCaptionsLegible(svg) {
    function fit() {
      var scale = svg.getBoundingClientRect().width / 740;
      if (scale > 0) svg.style.setProperty('--rl-cap', Math.max(CAPTION_UNITS, MIN_CAPTION_PX / scale).toFixed(1) + 'px');
    }
    fit();
    if (window.ResizeObserver) new ResizeObserver(fit).observe(svg);
  }

  /* ---------- Fig. 1: the cartridge ---------- */
  var HANDLE = [[96, 13], [124, 19], [170, 23], [230, 23], [282, 18], [336, 11], [392, 9], [446, 10], [476, 13], [490, 15]];
  function halfHeight(x) {
    for (var i = 1; i < HANDLE.length; i++) if (x <= HANDLE[i][0]) {
      var u = (x - HANDLE[i - 1][0]) / (HANDLE[i][0] - HANDLE[i - 1][0]);
      return HANDLE[i - 1][1] + u * (HANDLE[i][1] - HANDLE[i - 1][1]);
    }
    return HANDLE[HANDLE.length - 1][1];
  }
  function drawCartridgeHandle(g) {
    var top = HANDLE.map(function (k) { return [k[0], AXIS - k[1]]; });
    var bottom = HANDLE.map(function (k) { return [k[0], AXIS + k[1]]; }).reverse();
    var first = HANDLE[0], last = HANDLE[HANDLE.length - 1];
    el('path', { 'class': 'rl-o', d: 'M' + first[0] + ' ' + (AXIS - first[1]) + smoothThrough(top) + 'L' + last[0] + ' ' + (AXIS + last[1]) +
      smoothThrough(bottom) + 'A' + first[1] + ' ' + first[1] + ' 0 0 1 ' + first[0] + ' ' + (AXIS - first[1]) + 'Z' }, g);
    // rubber grip panel with ribs
    var gx = [136, 170, 230, 262], inset = 5;
    var gTop = gx.map(function (x) { return [x, AXIS - halfHeight(x) + inset]; });
    var gBot = gx.map(function (x) { return [x, AXIS + halfHeight(x) - inset]; }).reverse();
    var rEnd = halfHeight(262) - inset, rStart = halfHeight(136) - inset;
    el('path', { 'class': 'rl-d', d: 'M' + gTop[0][0] + ' ' + r1(gTop[0][1]) + smoothThrough(gTop) +
      'A' + r1(rEnd) + ' ' + r1(rEnd) + ' 0 0 1 ' + gBot[0][0] + ' ' + r1(gBot[0][1]) + smoothThrough(gBot) +
      'A' + r1(rStart) + ' ' + r1(rStart) + ' 0 0 1 ' + gTop[0][0] + ' ' + r1(gTop[0][1]) + 'Z' }, g);
    var ribs = '';
    for (var x = 146; x <= 252; x += 7) { var h = halfHeight(x) - 10; ribs += 'M' + x + ' ' + r1(AXIS - h) + 'V' + r1(AXIS + h); }
    el('path', { 'class': 'rl-d', d: ribs }, g);
    // shadow contour along the underside of the neck
    var shade = HANDLE.slice(4, 9).map(function (k) { return [k[0], AXIS + k[1] - 4]; });
    el('path', { 'class': 'rl-d', d: 'M' + shade[0][0] + ' ' + shade[0][1] + smoothThrough(shade) }, g);
    // the proprietary dock: a pivot yoke and a keyed tongue only one brand's head fits
    el('rect', { 'class': 'rl-o', x: 490, y: AXIS - 17, width: 40, height: 34, rx: 3 }, g);
    el('path', { 'class': 'rl-d', d: 'M500 ' + (AXIS - 17) + 'V' + (AXIS - 9) + 'M500 ' + (AXIS + 9) + 'V' + (AXIS + 17) }, g);
    el('circle', { 'class': 'rl-o', cx: 513, cy: AXIS, r: 6 }, g);
    el('circle', { 'class': 'rl-dot', cx: 513, cy: AXIS, r: 2 }, g);
    var key = [[530, -12], [538, -12], [538, -20], [548, -20], [548, -6], [544, -6], [544, 6], [548, 6], [548, 20], [538, 20], [538, 12], [530, 12]];
    el('path', { 'class': 'rl-o', d: 'M' + key.map(function (p) { return p[0] + ' ' + (AXIS + p[1]); }).join('L') + 'Z' }, g);
  }

  // One cartridge head, drawn upright about its centre; stroke stays constant as it scales (--s).
  function CartridgeHead(parent) {
    var g = this.g = el('g', { 'class': 'rl-head' }, parent);
    el('rect', { 'class': 'rl-o', x: -38, y: -100, width: 76, height: 200, rx: 10 }, g);
    var fins = '';
    for (var y = -90; y <= 90; y += 8) fins += 'M-33 ' + y + 'H-26';
    el('path', { 'class': 'rl-d rl-fine', d: fins }, g);
    el('path', { 'class': 'rl-d', d: 'M-22 -94V94' }, g);
    var blades = '';
    [-15, -8, -1, 6, 13].forEach(function (x) { blades += 'M' + x + ' -93V93'; });
    el('path', { 'class': 'rl-d', d: blades }, g);
    el('rect', { 'class': 'rl-o', x: -19, y: -74, width: 35, height: 8 }, g);
    el('rect', { 'class': 'rl-o', x: -19, y: 66, width: 35, height: 8 }, g);
    el('rect', { 'class': 'rl-o', x: 18, y: -93, width: 15, height: 186, rx: 7 }, g);
    el('path', { 'class': 'rl-d rl-fine', d: hatch(19.5, -86, 31.5, 86, 7, 1) }, g);
    this.place(DOCK_X, AXIS, 0, ZOOM, 0);
  }
  CartridgeHead.prototype.place = function (x, y, angle, scale, opacity) {
    this.g.setAttribute('transform', 'translate(' + r1(x) + ' ' + r1(y) + ') rotate(' + r1(angle) + ') scale(' + scale.toFixed(3) + ')');
    this.g.style.setProperty('--s', scale.toFixed(3));
    this.g.style.opacity = opacity;
    this.g.classList.toggle('is-small', scale < 0.75);   // fins and hatching would clog at pile size
  };

  function cycleStarts() {
    var starts = [], t = CART.calloutGone;
    CART.cycles.forEach(function (d) { starts.push(t); t += d; });
    return starts;
  }
  var STARTS = cycleStarts();
  function landedAt(i) { return STARTS[i] + HEAD_LANDS * CART.cycles[i]; }

  function CartridgeFigure(svg, strings) {
    this.svg = svg;
    var g = el('g', { 'class': 'rl-live' }, svg);
    drawFloor(g);
    this.months = new MonthRow(g, 'rl-fill', strings.months);
    this.counter = new Counter(g);
    drawCartridgeHandle(el('g', { transform: ASSEMBLY, style: '--s:' + ZOOM }, g));
    var heads = el('g', {}, g);
    this.heads = [];
    for (var i = 0; i <= PILE.length; i++) this.heads.push(new CartridgeHead(heads));
    var over = el('g', { transform: ASSEMBLY, style: '--s:' + ZOOM }, g);
    this.clicks = el('path', { 'class': 'rl-d rl-click', d: 'M522 210l-10-10M527 206l-2-13M518 214l-13-1M522 426l-10 10M527 430l-2 13M518 422l-13 1' }, over);
    this.buildCallout(over);
    this.cost = { low: +svg.dataset.costLow, high: +svg.dataset.costHigh, highText: svg.dataset.costHighText, caption: strings.year };
    keepCaptionsLegible(svg);
  }
  CartridgeFigure.prototype.buildCallout = function (g) {
    var c = this.callout = el('g', { 'class': 'rl-callout' }, g), cx = 522, cy = AXIS, r = 50, n = 26, ring = '';
    for (var i = 0; i < n; i++) {
      var a0 = -Math.PI / 2 + i * 2 * Math.PI / n, a1 = a0 + 0.55 * 2 * Math.PI / n;
      ring += 'M' + r1(cx + r * Math.cos(a0)) + ' ' + r1(cy + r * Math.sin(a0)) + 'A' + r + ' ' + r + ' 0 0 1 ' + r1(cx + r * Math.cos(a1)) + ' ' + r1(cy + r * Math.sin(a1));
    }
    this.ringDashes = [];
    var self = this;
    ring.split('M').filter(Boolean).forEach(function (seg) { self.ringDashes.push(el('path', { 'class': 'rl-d', d: 'M' + seg }, c)); });
    this.leader = el('path', { 'class': 'rl-d', d: 'M501 273L476 209', pathLength: 1, 'stroke-dasharray': 1 }, c);
    // the padlock sits above the dock, in the space the cost counter takes over in step 2
    var lock = this.lock = el('g', {}, el('g', { transform: 'translate(12 -264)' }, c));
    el('path', { 'class': 'rl-o rl-nofill', d: 'M422 414V400A18 18 0 0 1 458 400V414M430 414V401A10 10 0 0 1 450 401V414' }, lock);
    el('rect', { 'class': 'rl-o', x: 413, y: 414, width: 54, height: 52, rx: 4 }, lock);
    el('path', { 'class': 'rl-d', d: 'M454 419V461M459 419V461M463.5 419V461' }, lock);
    el('path', { 'class': 'rl-o', d: 'M437 438.6A5.5 5.5 0 1 1 443 438.6L444.5 451H435.5Z' }, lock);
  };
  CartridgeFigure.prototype.headPose = function (i, t) {
    var arriveA, arriveB, snap, prev = i - 1;
    if (i === 0) return this.firstHeadPose(t);
    var d = CART.cycles[prev], s = STARTS[prev];
    arriveA = s + 0.28 * d; arriveB = s + HEAD_LANDS * d; snap = s + HEAD_SNAPS * d;
    if (t < arriveA) return null;
    if (t < arriveB) { var u = progress(t, arriveA, arriveB); return [DOCK_X + 12 + 198 * (1 - easeOut(u)), AXIS, 0, ZOOM, clamp(u / 0.35, 0, 1)]; }
    if (t < snap) return [DOCK_X + 12 * (1 - easeIn(progress(t, arriveB, snap))), AXIS, 0, ZOOM, 1];
    if (i >= PILE.length || t < STARTS[i]) return [DOCK_X, AXIS, 0, ZOOM, 1];
    return this.fallingPose(i, t);
  };
  CartridgeFigure.prototype.fallingPose = function (i, t) {
    var dur = CART.cycles[i], v = (t - STARTS[i]) / dur, slot = PILE[i];
    if (v < 0.12) { var k = easeOut(v / 0.12); return [DOCK_X + 10 * k, AXIS, 4 * k, ZOOM, 1]; }
    if (v < HEAD_LANDS) {
      var f = (v - 0.12) / 0.5;
      return [DOCK_X + 10 + (slot[0] - DOCK_X - 10) * f, AXIS + (slot[1] - AXIS) * f * f, 4 + (slot[2] - 4) * easeInOut(f),
        ZOOM + (PILE_SCALE - ZOOM) * easeOut(f), 1];
    }
    var w = clamp((v - HEAD_LANDS) / 0.13, 0, 1), settle = Math.sin(Math.PI * w) * (1 - w);
    return [slot[0], slot[1] - 5 * settle, slot[2] + 5 * settle, PILE_SCALE, 1];
  };
  CartridgeFigure.prototype.firstHeadPose = function (t) {
    if (t < CART.arrive[0]) return null;
    if (t < CART.arrive[1]) {
      var u = progress(t, CART.arrive[0], CART.arrive[1]);
      return [DOCK_X + HOLD_GAP + 170 * (1 - easeOut(u)), AXIS, 0, ZOOM, clamp(u / 0.35, 0, 1)];
    }
    if (t < CART.hold) return [DOCK_X + HOLD_GAP, AXIS, 0, ZOOM, 1];
    if (t < CART.snap) return [DOCK_X + HOLD_GAP * (1 - easeIn(progress(t, CART.hold, CART.snap))), AXIS, 0, ZOOM, 1];
    if (t < STARTS[0]) return [DOCK_X, AXIS, 0, ZOOM, 1];
    return this.fallingPose(0, t);
  };
  CartridgeFigure.prototype.lastSnap = function (t) {
    var snaps = [CART.snap];
    for (var i = 0; i < PILE.length; i++) snaps.push(STARTS[i] + HEAD_SNAPS * CART.cycles[i]);
    var latest = -Infinity;
    snaps.forEach(function (s) { if (s <= t) latest = s; });
    return latest;
  };
  CartridgeFigure.prototype.monthsAt = function (t) {
    var months = 0;
    for (var i = 0; i < STARTS.length; i++) months += progress(t, STARTS[i], STARTS[i] + CART.cycles[i]);
    return months;
  };
  CartridgeFigure.prototype.costText = function (landed) {
    var c = this.cost;
    if (landed === 0) return inPounds(0);
    if (landed === MONTHS.count) return inPounds(c.low) + ' to ' + inPounds(c.highText);
    return inPounds(Math.round(c.low * landed / MONTHS.count)) + ' to ' + inPounds(Math.round(c.high * landed / MONTHS.count));
  };
  CartridgeFigure.prototype.render = function (t) {
    var i;
    for (i = 0; i < this.heads.length; i++) {
      var pose = this.headPose(i, t);
      if (pose) this.heads[i].place(pose[0], pose[1], pose[2], pose[3], pose[4]);
      else this.heads[i].g.style.opacity = 0;
    }
    var since = t - this.lastSnap(t);
    this.clicks.style.opacity = since >= 0 && since < 0.22 ? (1 - since / 0.22).toFixed(3) : 0;
    var fadeOut = 1 - progress(t, CART.stepOne, CART.calloutGone);
    var ringShown = progress(t, CART.ring[0], CART.ring[1]) * this.ringDashes.length;
    this.ringDashes.forEach(function (dash, n) { dash.style.opacity = n < ringShown ? 1 : 0; });
    this.leader.setAttribute('stroke-dashoffset', (1 - easeOut(progress(t, CART.leader[0], CART.leader[1]))).toFixed(3));
    var lockIn = easeOut(progress(t, CART.lock[0], CART.lock[1]));
    this.lock.style.opacity = lockIn;
    this.lock.setAttribute('transform', 'translate(0 ' + r1(-8 * (1 - lockIn)) + ')');
    this.callout.style.opacity = fadeOut;
    var landed = 0;
    for (i = 0; i < PILE.length; i++) if (t >= landedAt(i)) landed++;
    var shown = progress(t, CART.stepOne + 0.25, CART.stepOne + 0.55);
    var captionIn = landed === MONTHS.count ? progress(t, landedAt(LAST_MONTH), landedAt(LAST_MONTH) + 0.4) : 0;
    var text = this.costText(landed);
    this.counter.render(text, this.cost.caption, shown, captionIn);
    this.months.render(this.monthsAt(t));
  };

  /* ---------- Fig. 2: one safety razor (Kronos three-piece: cap, blade, base plate, handle) ----------
     Traced from the true side elevation "At perfect 90 degrees.png" (exploded: cap, blade, base plate, handle).
     Coordinates are that photo's pixels with the parts closed up: x across from the centre line, y down from
     the top of the cap. Measured there: cap 472 long and 56 deep at its ends (0.115 of its length, from the close-up of the cap); guide pins 34 long at 137 either side of
     a 108 long threaded stud; base plate 504 long with upturned end lips and 46 comb bars; handle 100 across
     the knurl and 1145 long, its polished neck 86 at the top, 62 at the seam and 26 per cent of the length.
     TRACE_TO_PLATE lays the upright drawing on its side, head right. */
  var TRACE_SCALE = 0.47;                           // plate units per photo pixel: razor 1230 px long, 578 units
  var HEAD_X = 612;                                 // plate x of the top of the cap
  var TRACE_TO_PLATE = 'matrix(0 ' + TRACE_SCALE + ' ' + -TRACE_SCALE + ' 0 ' + HEAD_X + ' ' + AXIS + ')';
  var RAZOR_PX = {
    bladeY: 49,                                     // in the slot between the guard's deck and the cap's underside
    neck: { top: 85, seam: 223, bottom: 367 }, collar: [367, 381],
    knurl: { x0: -50, x1: 50, y0: 381, y1: 1193, pitch: 12, stroke: 1.2 }, end: 1230
  };
  var CAP_TRAVEL_PX = 185, UNSCREW_PX = 30;
  function plateFromPhoto(x, y) { return [HEAD_X - TRACE_SCALE * y, AXIS + TRACE_SCALE * x]; }

  /* The head is Rory's own drawing (6 Oct 2026), supplied as the half below the centre line in plate
     orientation at 1/2.236 of plate scale; HEAD_ART holds it mirrored about its axis (v = 48.248) into
     closed shapes. headArt() places those units in the traced group: the guard's handle-side face
     (u = 13.9308) sits on the top of the handle. */
  var HEAD_ART = {
    scale: 2.236, axis: 48.248, handleFace: 13.9308, toothPitch: 1.8085, teethEachSide: 23,
    guard: 'M20.498 48.248V90.248H22.5943L22.498 95.248C24.5133 95.4496 31.998 99.6435 31.998 100.248' +
      'C31.998 101.457 27.867 103.509 24.998 103.248C19.5 102.748 15.3414 100.76 13.9308 95.1179V1.3781' +
      'C15.3414 -4.264 19.5 -6.252 24.998 -6.752C27.867 -7.013 31.998 -4.961 31.998 -3.752' +
      'C31.998 -3.1475 24.5133 1.0464 22.498 1.248L22.5943 6.248H20.498Z',
    rail: 'M15.998 6.748V89.748',
    cap: 'M23 50.832V95.2481L32.2321 99.2481C32.2321 99.2481 33.7714 95.7481 35.3143 87.4792' +
      'C36.8572 79.2103 36.3 62 36.3 48.248C36.3 34.496 36.8572 17.2857 35.3143 9.0168' +   // top joined smoothly across the centre line
      'C33.7714 0.7479 32.2321 -2.7521 32.2321 -2.7521L23 1.2479Z'
  };
  var HEAD_K = HEAD_ART.scale / TRACE_SCALE;        // photo pixels per unit of the head drawing
  function headArt(parent) {
    return el('g', { transform: 'matrix(0 ' + -HEAD_K + ' ' + HEAD_K + ' 0 ' + r1(-HEAD_ART.axis * HEAD_K) + ' ' +
      r1(RAZOR_PX.neck.top + HEAD_ART.handleFace * HEAD_K) + ')', style: '--s:' + HEAD_ART.scale }, parent);
  }
  function drawCap(g) {                             // deep cap: flat underside, corners sloping into the guard's end lips
    el('path', { 'class': 'rl-o', d: HEAD_ART.cap }, headArt(g));
  }
  function drawCapUnderside(g) {                    // two stubby guide pins and the longer threaded stud, under the cap
    var thread = '';
    for (var y = 92; y <= 140; y += 8) thread += 'M-23 ' + y + 'l46 5';
    el('rect', { 'class': 'rl-o', x: -23, y: 34, width: 46, height: 118, rx: 10 }, g);
    el('path', { 'class': 'rl-d', d: thread, style: 'stroke-width:calc(1.2px / var(--s, 1))' }, g);
    el('rect', { 'class': 'rl-o', x: -157, y: 34, width: 40, height: 44, rx: 13 }, g);
    el('rect', { 'class': 'rl-o', x: 117, y: 34, width: 40, height: 44, rx: 13 }, g);
  }
  function drawBasePlate(g) {                       // guard: deck with raised end pads, end lips wrapping the cap, comb bars on a rail
    var art = headArt(g), teeth = '';
    el('path', { 'class': 'rl-o', d: HEAD_ART.guard }, art);
    for (var n = -HEAD_ART.teethEachSide; n <= HEAD_ART.teethEachSide; n++) teeth += 'M18.998 ' + roundToThousandths(HEAD_ART.axis + n * HEAD_ART.toothPitch) + 'H15.998';
    el('path', { 'class': 'rl-d', d: teeth, style: 'stroke-width:calc(1.15px / var(--s, 1))' }, art);
    el('path', { 'class': 'rl-d', d: HEAD_ART.rail, style: 'stroke-width:calc(1.45px / var(--s, 1))' }, art);
  }
  function roundToThousandths(n) { return Math.round(n * 1000) / 1000; }
  function drawHandle(g) {                          // neck widest at the top, waisted at the seam, then diamond knurl and a plain rounded end
    var k = RAZOR_PX.knurl, n = RAZOR_PX.neck;
    el('path', { 'class': 'rl-o', d: 'M-43 ' + n.top + 'C-41 130 -34 190 -31 ' + n.seam + 'H31C34 190 41 130 43 ' + n.top + 'Z' }, g);
    el('path', { 'class': 'rl-o', d: 'M-31 ' + n.seam + 'C-33 270 -42 330 -47 ' + n.bottom + 'H47C42 330 33 270 31 ' + n.seam + 'Z' }, g);
    el('path', { 'class': 'rl-d', d: 'M-42 93H42M24 100C23 140 19 185 18 216M18 232C20 275 27 325 31 360',
      style: 'stroke-width:calc(1.5px / var(--s, 1))' }, g);
    el('rect', { 'class': 'rl-o', x: k.x0, y: k.y1 - 20, width: k.x1 - k.x0, height: RAZOR_PX.end - k.y1 + 20, rx: 18 }, g);
    el('rect', { 'class': 'rl-o', x: k.x0, y: k.y0, width: k.x1 - k.x0, height: k.y1 - k.y0 }, g);
    var knurl = el('path', { 'class': 'rl-d', style: 'stroke-width:calc(' + k.stroke + 'px / var(--s, 1))' }, g);
    el('rect', { 'class': 'rl-o', x: -49, y: RAZOR_PX.collar[0], width: 98, height: RAZOR_PX.collar[1] - RAZOR_PX.collar[0], rx: 3 }, g);
    return knurl;
  }
  function drawKnurl(path, phase) {
    var k = RAZOR_PX.knurl;
    path.setAttribute('d', hatch(k.x0, k.y0, k.x1, k.y1, k.pitch, 1, phase) + hatch(k.x0, k.y0, k.x1, k.y1, k.pitch, -1, phase));
  }
  function tracedGroup(parent) { return el('g', { transform: TRACE_TO_PLATE, style: '--s:' + TRACE_SCALE }, parent); }

  /* Blades, in plate units. In this side elevation a blade in the head is edge-on: a hairline between cap
     and base plate, seated or lifted clear of the lips. It turns broadside only once it is out of the head. */
  var SEAT = (function () { var p = plateFromPhoto(0, RAZOR_PX.bladeY); return { x: p[0], y: p[1], scale: 0.96 }; })();
  var GAP_X = HEAD_X + 8;                                  // clear of the base plate's lips, short of the lifted stud
  var EXIT_Y = 520, EXIT_SCALE = 0.6;               // below the head, clear of the floor
  var FORESHORTENED = 0.03, EDGE_ON = 0.03, FLAT = 0.05;
  var STACK_X = 440, STACK_SCALE = 0.53, STACK_STEP = 6;
  var STACK_JITTER = [0, 1, -1, 1, -1, 1, -1, 1, -1, 1, -1, 0];
  var ARRIVE_FROM = { x: 672, y: 560 };
  function stackSlot(i) { return [STACK_X + STACK_JITTER[i], FLOOR.y - 2.5 - STACK_STEP * i]; }

  // The blade is 196 long inside a cap footprint of about 200: only its edge stands proud, never its ends.
  function bladeOutline(fx) {
    if (fx < 0.08) return 'M0 -98V98';            // edge-on or lying flat: one hairline, so stacked blades stay countable
    if (fx < 0.15) { var w = 52 * fx; return 'M' + r1(-w) + ' -98H' + r1(w) + 'V98H' + r1(-w) + 'Z'; }
    var pts = [[-47, -98], [-6, -98], [-6, -91], [6, -91], [6, -98], [47, -98], [52, -93], [52, 93], [47, 98], [6, 98],
      [6, 91], [-6, 91], [-6, 98], [-47, 98], [-52, 93], [-52, -93]];
    return 'M' + pts.map(function (p) { return r1(p[0] * fx) + ' ' + p[1]; }).join('L') + 'Z';
  }
  function bladeSlot(fx) {                          // the centre slot: three openings joined by a narrow cut
    var holes = [-51, 0, 51], hw = 2.5 * fx, r = 9, rx = r * fx, dy = Math.sqrt(r * r - 2.5 * 2.5), end = 77;
    var d = 'M' + r1(-hw) + ' ' + -end + 'A' + r1(hw) + ' ' + r1(hw) + ' 0 0 1 ' + r1(hw) + ' ' + -end;
    holes.forEach(function (cy) { d += 'L' + r1(hw) + ' ' + r1(cy - dy) + 'A' + r1(rx) + ' ' + r + ' 0 1 1 ' + r1(hw) + ' ' + r1(cy + dy); });
    d += 'L' + r1(hw) + ' ' + end + 'A' + r1(hw) + ' ' + r1(hw) + ' 0 0 1 ' + r1(-hw) + ' ' + end;
    holes.slice().reverse().forEach(function (cy) { d += 'L' + r1(-hw) + ' ' + r1(cy + dy) + 'A' + r1(rx) + ' ' + r + ' 0 1 1 ' + r1(-hw) + ' ' + r1(cy - dy); });
    return d + 'Z';
  }
  function DoubleEdgeBlade(parent) {
    var g = this.g = el('g', { 'class': 'rl-blade' }, parent);
    this.outline = el('path', { 'class': 'rl-o' }, g);
    this.bevel = el('path', { 'class': 'rl-d' }, g);
    this.centreSlot = el('path', { 'class': 'rl-o' }, g);
    this.fx = null;
  }
  DoubleEdgeBlade.prototype.place = function (x, y, angle, scale, fx, opacity) {
    this.g.setAttribute('transform', 'translate(' + r1(x) + ' ' + r1(y) + ') rotate(' + r1(angle) + ') scale(' + scale.toFixed(3) + ')');
    this.g.style.setProperty('--s', scale.toFixed(3));
    this.g.style.opacity = opacity;
    fx = Math.round(fx * 100) / 100;
    if (fx === this.fx) return;
    this.fx = fx;
    this.outline.setAttribute('d', bladeOutline(fx));
    this.bevel.setAttribute('d', fx > 0.3 ? 'M' + r1(-46 * fx) + ' -91V91M' + r1(46 * fx) + ' -91V91' : '');
    this.centreSlot.setAttribute('d', fx >= 0.2 ? bladeSlot(fx) : '');
  };

  // Phases as shares of a cycle. The razor is assembled once, in the first cycle: the blade goes on the pins,
  // the cap closes, the handle tightens. After that the head stays shut and each month's spent blade slides
  // out of the bottom of the head, on Fig. 1's cadence.
  var FIRST_SWAP = { into: [0, 0.42], capDown: [0.4, 0.58], screw: [0.52, 0.68], out: [0.72, 1] };
  var REPEAT_SWAP = { out: [0.25, 0.95] };
  var SAFETY = { start: 0.4, firstSwap: 2.6, hold: 1.5, captionFade: 0.4 };
  SAFETY.cycles = [SAFETY.firstSwap].concat(CART.cycles.slice(1));   // one blade a month, on Fig. 1's month cadence
  SAFETY.starts = (function () { var t = SAFETY.start; return SAFETY.cycles.map(function (d) { var s = t; t += d; return s; }); })();
  SAFETY.yearEnd = SAFETY.starts[LAST_MONTH] + SAFETY.cycles[LAST_MONTH];
  SAFETY.end = SAFETY.yearEnd + SAFETY.captionFade + SAFETY.hold;
  SAFETY.still = SAFETY.end - 0.1;

  function SafetyFigure(svg, strings) {
    this.svg = svg;
    var g = el('g', { 'class': 'rl-live' }, svg);
    drawFloor(g);
    this.months = new MonthRow(g, 'rl-fill', strings.months);
    this.counter = new Counter(g);
    var razor = el('g', { 'class': 'rl-gilt' }, g);
    this.capUnder = el('g', {}, tracedGroup(razor));
    drawCapUnderside(this.capUnder);
    this.handle = el('g', {}, tracedGroup(razor));
    this.knurl = drawHandle(this.handle);
    drawKnurl(this.knurl, 0);
    drawBasePlate(tracedGroup(razor));
    var blades = el('g', {}, razor);
    this.blades = [];
    for (var i = 0; i < MONTHS.count; i++) this.blades.push(new DoubleEdgeBlade(blades));
    this.cap = el('g', {}, tracedGroup(razor));
    drawCap(this.cap);
    this.cost = { year: +svg.dataset.costYear, caption: strings.year, yearText: svg.dataset.costYearText };
    keepCaptionsLegible(svg);
  }
  SafetyFigure.prototype.phases = function (i) { return i === 0 ? FIRST_SWAP : REPEAT_SWAP; };
  SafetyFigure.prototype.at = function (i, t, phaseWindow) {
    var s = SAFETY.starts[i], d = SAFETY.cycles[i];
    return progress(t, s + phaseWindow[0] * d, s + phaseWindow[1] * d);
  };
  SafetyFigure.prototype.seatedPose = function (lift) {
    return [SEAT.x, SEAT.y, 0, SEAT.scale, EDGE_ON + (FORESHORTENED - EDGE_ON) * lift, 1];
  };
  // Leaving: edge-on down out of the closed head, then broadside and flat onto the stack.
  SafetyFigure.prototype.leavingPose = function (i, f) {
    var slot = stackSlot(i);
    if (f < 0.4) { var d = easeInOut(f / 0.4); return [SEAT.x, SEAT.y + (EXIT_Y - SEAT.y) * d, 0, SEAT.scale + (EXIT_SCALE - SEAT.scale) * d, EDGE_ON, 1]; }
    var flight = (f - 0.4) / 0.6, a = 1 - flight, cx = 600, cy = 500;
    var x = a * a * SEAT.x + 2 * a * flight * cx + flight * flight * slot[0], y = a * a * EXIT_Y + 2 * a * flight * cy + flight * flight * slot[1];
    var turn = Math.sin(Math.PI * flight), fx = flight < 0.5 ? EDGE_ON + (1 - EDGE_ON) * turn : Math.max(FLAT, turn);
    return [x, y, 90 * easeInOut(flight), EXIT_SCALE + (STACK_SCALE - EXIT_SCALE) * easeOut(flight), fx, 1];
  };
  // Arriving: from the lower right, lying broadside, up into the gap and across onto the pins.
  SafetyFigure.prototype.arrivingPose = function (f) {
    if (f < 0.45) {
      var approach = f / 0.45, a = 1 - approach, cx = 690, cy = 500;
      var x = a * a * ARRIVE_FROM.x + 2 * a * approach * cx + approach * approach * GAP_X, y = a * a * ARRIVE_FROM.y + 2 * a * approach * cy + approach * approach * EXIT_Y;
      return [x, y, 90 * (1 - easeInOut(approach)), STACK_SCALE + (EXIT_SCALE - STACK_SCALE) * easeIn(approach), 1 - (1 - FORESHORTENED) * easeIn(approach), clamp(approach / 0.25, 0, 1)];
    }
    if (f < 0.85) { var u = easeInOut((f - 0.45) / 0.4); return [GAP_X, EXIT_Y + (SEAT.y - EXIT_Y) * u, 0, EXIT_SCALE + (SEAT.scale - EXIT_SCALE) * u, FORESHORTENED, 1]; }
    return [GAP_X + (SEAT.x - GAP_X) * easeInOut((f - 0.85) / 0.15), SEAT.y, 0, SEAT.scale, FORESHORTENED, 1];
  };
  SafetyFigure.prototype.bladePose = function (i, t) {
    if (i === 0) {                                  // the one blade shown going in
      var arrive = this.at(0, t, FIRST_SWAP.into);
      if (arrive < 1) return this.arrivingPose(Math.max(arrive, 0.001));
    }
    var leave = this.at(i, t, this.phases(i).out);
    if (leave <= 0) return i === 0 ? this.seatedPose(0) : null;   // later blades stay unseen inside the closed head
    return this.leavingPose(i, leave);
  };
  SafetyFigure.prototype.capLift = function (t) { return 1 - easeInOut(this.at(0, t, FIRST_SWAP.capDown)); };
  SafetyFigure.prototype.render = function (t) {
    var lift = this.capLift(t), capShift = 'translate(0 ' + r1(-CAP_TRAVEL_PX * lift) + ')';
    this.cap.setAttribute('transform', capShift);
    this.capUnder.setAttribute('transform', capShift);
    var turn = 1 - easeInOut(this.at(0, t, FIRST_SWAP.screw));
    this.handle.setAttribute('transform', 'translate(0 ' + r1(UNSCREW_PX * turn) + ')');
    if (turn !== this.lastTurn) { drawKnurl(this.knurl, turn * RAZOR_PX.knurl.pitch * 2.5); this.lastTurn = turn; }
    var landed = 0, months = 0, i;
    for (i = 0; i < MONTHS.count; i++) {
      var pose = this.bladePose(i, t);
      if (pose) this.blades[i].place(pose[0], pose[1], pose[2], pose[3], pose[4], pose[5]);
      else this.blades[i].g.style.opacity = 0;
    }
    for (i = 0; i < MONTHS.count; i++) {
      if (this.at(i, t, this.phases(i).out) >= 1) landed++;
      months += progress(t, SAFETY.starts[i], SAFETY.starts[i] + SAFETY.cycles[i]);
    }
    this.months.render(months);
    var text = inPounds(landed >= MONTHS.count ? this.cost.yearText : Math.round(this.cost.year * landed / MONTHS.count));
    this.counter.render(text, this.cost.caption, 1, progress(t, SAFETY.yearEnd, SAFETY.yearEnd + SAFETY.captionFade));
  };
  /* ---------- module for the shared controller ---------- */
  // Captions are block settings, rendered as hidden strings beside the plate.
  function readStrings(plate) {
    var strings = {};
    ['year', 'months'].forEach(function (key) {
      var node = plate.querySelector('[data-rl-string="' + key + '"]');
      if (!node) throw new Error('Refill ledger: missing caption string "' + key + '"');
      strings[key] = node.textContent.trim();
    });
    return strings;
  }
  function mountRefillLedger(plate) {
    var strings = readStrings(plate);
    var fig1 = new CartridgeFigure(plate.querySelector('svg[data-fig="1"]'), strings);
    var fig2 = new SafetyFigure(plate.querySelector('svg[data-fig="2"]'), strings);
    return {
      fig1: fig1, fig2: fig2,
      segments: [[0, CART.stepOne], [CART.stepOne, CART.end], [0, SAFETY.end]],
      still: [CART.still, SAFETY.still]
    };
  }
  window.KGFigures = window.KGFigures || {};
  window.KGFigures['refill-ledger'] = mountRefillLedger;
})();
